/**
 * "Show-this-secret-exactly-once" dialog. Used by OIDC client create + rotate.
 * Cannot be dismissed by Escape or backdrop click; the "I've saved this in a
 * password manager" button is disabled for 5 seconds after the dialog opens.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEffect, useState, type ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';

const HOLD_MS = 5000;

export interface SecretOnceDialogProps {
  open: boolean;
  secret: string;
  label?: string;
  /** Optional extra content rendered below the secret block (e.g. the
   *  freshly-created OIDC client's allow-list — populated from the
   *  shipped backend response). */
  extras?: ReactNode;
  onAcknowledge: () => void;
}

export function SecretOnceDialog({
  open,
  secret,
  label,
  extras,
  onAcknowledge,
}: SecretOnceDialogProps) {
  const { t } = useCopy();
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset the hold/copied state when the dialog closes via a render-phase state
  // adjustment (React's "adjust state while rendering" pattern) instead of a
  // synchronous setState in an effect.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setReady(false);
      setCopied(false);
    }
  }

  // While open, arm the hold timer that enables the reveal/copy action.
  // `setReady(true)` here is asynchronous (timer callback), not a synchronous
  // effect-body setState.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setReady(true), HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      aria-labelledby="secret-once-title"
    >
      <DialogTitle id="secret-once-title">
        {label ?? t('oidc.secret.title', 'Save this secret now')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'oidc.secret.body',
            'This is the only time you will see this secret. Copy it to your password manager before closing.',
          )}
        </DialogContentText>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Box
            component="code"
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              wordBreak: 'break-all',
            }}
          >
            {secret}
          </Box>
          <Tooltip title={copied ? t('common.copied', 'Copied') : t('common.copy', 'Copy')}>
            <IconButton onClick={handleCopy} aria-label={t('common.copy', 'Copy')}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {extras ? <Box sx={{ mt: 2 }}>{extras}</Box> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={!ready} onClick={onAcknowledge}>
          {t('oidc.secret.acknowledge', "I've saved this in a password manager")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
